import React from 'react';
import { Modal } from 'antd';
import styles from './TermsAndConditions.module.scss';

interface TermsAndConditionsProps {
  open: boolean;
  onClose: () => void;
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({ open, onClose }) => {
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={780}
      centered
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', padding: '32px 40px' }}
    >
      <h1 className={styles.termsTitle}>Synergetics Marketplace</h1>
      <p className={styles.termsSubtitle}>Terms and Conditions</p>
      <hr className={styles.termsDivider} />
      <p className={styles.effectiveDate}>Effective Date: March 12, 2026</p>

      <h2 className={styles.sectionTitle}>1. Introduction and Acceptance</h2>
      <div className={styles.sectionContent}>
        <p>These Terms and Conditions ("Terms") govern access to and use of the Synergetics Marketplace ("Platform"), operated by UnifyGPT Inc. d/b/a Synergetics ("Synergetics," "we," "us," or "our"). By accessing, registering, listing, purchasing, or otherwise using the Platform, you agree to be bound by these Terms.</p>
        <p>These Terms apply to all users, including without limitation sellers, buyers, developers, and visitors (collectively, "Users"). If you do not agree to these Terms, you must not use the Platform.</p>
      </div>

      <h2 className={styles.sectionTitle}>2. Definitions</h2>
      <div className={styles.sectionContent}>
        <p><strong>"Digital Assets"</strong> means any products or services made available on the Platform, including but not limited to AI agents, avatars, skills, software modules, integrations, wearables, datasets, and related content.</p>
        <p><strong>"Seller"</strong> means any User who lists or offers Digital Assets on the Platform.</p>
        <p><strong>"Buyer"</strong> means any User who purchases, subscribes to, or otherwise uses Digital Assets.</p>
        <p><strong>"User Content"</strong> means any data, content, or materials uploaded, submitted, or generated by Users.</p>
      </div>

      <h2 className={styles.sectionTitle}>3. Role of Synergetics</h2>
      <div className={styles.sectionContent}>
        <p>Synergetics provides a technology platform that enables Sellers to list and distribute Digital Assets and Buyers to discover, purchase, and use such Digital Assets.</p>
        <p>Synergetics is not the creator, owner, or seller of third-party Digital Assets and is not a party to any agreement between Buyers and Sellers. Synergetics does not control, endorse, or guarantee the quality, legality, or functionality of Digital Assets.</p>
      </div>

      <h2 className={styles.sectionTitle}>4. Account Registration and Security</h2>
      <div className={styles.sectionContent}>
        <p>Users may be required to create an account. You agree to provide accurate and complete information and to maintain the confidentiality of your account credentials.</p>
        <p>You are responsible for all activities under your account. Synergetics is not liable for unauthorized access resulting from your failure to safeguard your credentials.</p>
      </div>

      <h2 className={styles.sectionTitle}>5. Seller Obligations</h2>
      <div className={styles.sectionContent}>
        <p>Sellers represent and warrant that:</p>
        <ul>
          <li>They have all necessary rights, licenses, and permissions to offer Digital Assets.</li>
          <li>Their Digital Assets do not infringe any intellectual property or proprietary rights.</li>
          <li>Their Digital Assets comply with applicable laws and regulations, including privacy and data protection laws.</li>
          <li>They will provide accurate descriptions and any applicable licensing or service terms.</li>
        </ul>
        <p>Sellers are solely responsible for support, maintenance, and performance of their Digital Assets.</p>
      </div>

      <h2 className={styles.sectionTitle}>6. Buyer Obligations</h2>
      <div className={styles.sectionContent}>
        <p>Buyers agree:</p>
        <ul>
          <li>To pay all applicable fees and charges.</li>
          <li>To use Digital Assets in compliance with applicable laws and any applicable licenses.</li>
          <li>Not to misuse the Platform, including generating unlawful or harmful content.</li>
        </ul>
        <p>Buyers acknowledge that Digital Assets may be subject to separate license terms provided by Sellers.</p>
      </div>

      <h2 className={styles.sectionTitle}>7. Fees and Payments</h2>
      <div className={styles.sectionContent}>
        <p>Synergetics may charge transaction fees, subscription fees, or other charges. All fees are non-refundable unless otherwise stated.</p>
        <p>Payments may be processed via fiat currency or platform-based tokens. Token balances are non-transferable except as expressly permitted.</p>
      </div>

      <h2 className={styles.sectionTitle}>8. Intellectual Property</h2>
      <div className={styles.sectionContent}>
        <p>Synergetics retains all rights in and to the Platform.</p>
        <p>Sellers retain ownership of their Digital Assets, subject to licenses granted to Buyers.</p>
        <p>Buyers receive a limited, non-exclusive, non-transferable license to use Digital Assets in accordance with applicable terms.</p>
      </div>

      <h2 className={styles.sectionTitle}>9. Disclaimers</h2>
      <div className={styles.sectionContent}>
        <p>THE PLATFORM AND ALL DIGITAL ASSETS ARE PROVIDED "AS IS" AND "AS AVAILABLE."</p>
        <p>SYNERGETICS DISCLAIMS ALL WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>
        <p>SYNERGETICS DOES NOT WARRANT THAT DIGITAL ASSETS WILL BE ERROR-FREE, ACCURATE, OR RELIABLE, INCLUDING OUTPUT GENERATED BY AI SYSTEMS.</p>
      </div>

      <h2 className={styles.sectionTitle}>10. Limitation of Liability</h2>
      <div className={styles.sectionContent}>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, SYNERGETICS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.</p>
        <p>SYNERGETICS' TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNTS PAID BY THE USER TO SYNERGETICS IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.</p>
      </div>

      <h2 className={styles.sectionTitle}>11. Indemnification</h2>
      <div className={styles.sectionContent}>
        <p>You agree to indemnify and hold harmless Synergetics and its affiliates from any claims arising out of:</p>
        <ul>
          <li>Your use of the Platform;</li>
          <li>Your violation of these Terms;</li>
          <li>Your infringement of any rights of a third party.</li>
        </ul>
      </div>

      <h2 className={styles.sectionTitle}>12. Suspension and Termination</h2>
      <div className={styles.sectionContent}>
        <p>Synergetics may suspend or terminate access to the Platform at its discretion, including for violations of these Terms.</p>
        <p>Upon termination, your right to use the Platform ceases immediately.</p>
      </div>

      <h2 className={styles.sectionTitle}>13. Privacy and Data</h2>
      <div className={styles.sectionContent}>
        <p>Use of the Platform is subject to Synergetics' Privacy Policy.</p>
        <p>Users are responsible for ensuring compliance with applicable data protection laws when using or deploying Digital Assets.</p>
      </div>

      <h2 className={styles.sectionTitle}>14. Governing Law and Dispute Resolution</h2>
      <div className={styles.sectionContent}>
        <p>These Terms are governed by the laws of the State of California.</p>
        <p>Any disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.</p>
      </div>

      <h2 className={styles.sectionTitle}>15. Modifications</h2>
      <div className={styles.sectionContent}>
        <p>Synergetics reserves the right to modify these Terms at any time. Continued use of the Platform constitutes acceptance of updated Terms.</p>
      </div>

      <h2 className={styles.sectionTitle}>16. Entire Agreement</h2>
      <div className={styles.sectionContent}>
        <p>These Terms constitute the entire agreement between you and Synergetics regarding the Platform.</p>
        <p>By clicking "I Agree" or by using the Synergetics Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms.</p>
      </div>
    </Modal>
  );
};

export default TermsAndConditions;
